import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { ScopeTabComponent } from '../../scope-tab/scope-tab.component'
import { ScopeVersion } from '@app/core/model/scope-version'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
} from 'rxjs'
import { select, Store } from '@ngrx/store'
import { ScopeOverviewNavigationService } from '@app/features/scope-overview/service/scope-overview-navigation.service'
import { FindReplaceService } from '@app/features/scope-overview/service/find-replace.service'
import { FindReplaceRole } from '@app/features/scope-overview/model/find-repace-roles.model'
import { ScopingSelectors } from '@app/features/scoping/store/selectors/scoping.selector'
import { ScopeOverviewFilter, ScopeOverviewFilterDefaults } from '@app/core/model/scope-overview-filter.model'
import { TableColumnPreferences } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { TypedFilterValue, GenericFilterValue } from '@app/core/model/filter-option.interface'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable, SelectedDeliverable } from '@app/features/scoping/models/deliverable.model'
import { Department } from '@app/features/scoping/models/department.model'
import { Role } from '@app/features/scoping/models/role.model'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { isEqual } from 'lodash'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { LanguageService } from '@app/core/service/language.service'
import { SNACKBAR_LENGTH_SHORT, SnackbarEventType, SnackbarService } from '@app/shared/utils/snackbar.service'
import { resolveShowUseRetainedTeamToggle } from '@app/features/scope-overview/utils/resolveShowUseRetainedTeamToggle.util'
import { AddDeliverableEvent, FeeEvent } from '../models'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { untilDestroyed } from '@app/shared/utils/utils'
import { BootstrapSelectors } from '@app/core/store/selectors/bootstrap.selector'
import { FindRolesSearchParams } from '@app/core/model/definitions/findRoleSearchParams.interface'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { generateBreadCrumbs } from '../utils'
import { FormControl } from '@angular/forms'
import { plainToInstance } from 'class-transformer'
import { DeliverableType } from '@app/features/scope-overview/model/deliverable-type'
import { CreateDeliverableModalComponent } from '../../scope-tab/create-deliverable-modal/create-deliverable-modal.component'
import { LibraryTemplateType } from '@app/features/library-management/enums'
import { LibraryItemType } from '@app/features/library-management/store'
import { Money } from '@app/features/scope-overview/model/money.model'
import { Router } from '@angular/router'
import { FeeItem } from '@app/features/scope-overview/model/fee-item.model'
import { AddEditFeeModalComponent } from '../../scope-tab/add-edit-fee-modal/add-edit-fee-modal.component'
import { MyScopesService } from '@app/features/scoping/service/scoping.service'
import { ApprovalsService } from '@app/features/scope-overview/service/approvals.service'
import { Timeout } from '@app/core/model/enums/timeout.enum'
import { AddRolesModalConfig, AddRolesModalComponent } from '../../add-roles-modal/add-roles-modal.component'
import { AddTpcModalComponent } from '../../add-tpc-modal/add-tpc-modal.component'
import { TrackTradeModalConfig, TrackTradeModalComponent } from '../../track-trade-modal/track-trade-modal.component'
import { AddEditDiscountModalComponent } from '../../scope-tab/add-edit-discount-modal/add-edit-discount-modal.component'
import { EditMsaModalComponent } from '../../scope-tab/edit-msa-modal/edit-msa-modal.component'
import { ScopeByRoleTabComponent } from '../../scope-by-role-tab/scope-by-role-tab.component'
import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { Preference } from '@app/core/model/user-preferences.interface'
import {
  ReviewSubmitModalConfig,
  ReviewSubmitModalComponent,
} from '../../review-submit-modal/review-submit-modal.component'
import { NewTask } from '@app/core/model/new-task.model'
import { AddTaskModalComponent } from '../../scope-tab/add-task-modal/add-task-modal.component'
import { ScopeApprovalFlowComponent } from '@app/features/scope-overview/approval-flow/scope-approval-flow/scope-approval-flow.component'
import { UserSettingsService } from '@app/features/scope-overview/service/user-settings.service'
import { ExportModalConfig } from '@app/features/scope-overview/model/export-modal-config'
import { IntegrationModalConfig } from '@app/features/scope-overview/model/integration-modal-config'
import { ExportTrafficModal } from '../../export-traffic/export-traffic.component'
import { IntegrationTrafficModal } from '../../integration-traffic/integration-traffic.component'
import { ScopeTraffickingService } from '@app/features/scope-overview/service/scope-trafficking.service'
import { ChangeRequestCreatePayload } from '@app/features/scope-overview/change-request/model'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ScopeTeamService } from '@app/features/scope-overview/service/scope-team.service'
import { ExportModalComponent } from '../../export-modal/export-modal.component'
import { DialogEventsService } from '@app/features/scope-overview/service/dialog-events.service'
import { ExportExcelModal } from '../../export-excel/export-excel.component'
import { UpdateDeliverablePayload } from '@app/shared/components/scope-elements/models'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import { mapScopeComponentToApi } from '@app/features/scope-overview/utils/map-scope-entity.utils'
import { MoveDeliverablePayload } from '@app/features/scope-overview/model/move-deliverable.payload.model'

@Component({
  selector: 'scope-overview-scope',
  standalone: true,
  imports: [CommonModule, ScopeTabComponent, ScopeByRoleTabComponent, ScopeApprovalFlowComponent],
  templateUrl: './scope-overview-scope.component.html',
  styleUrls: ['./scope-overview-scope.component.scss'],
})
export class ScopeOverviewScopeComponent implements OnInit {
  currentScope$: Observable<ScopeVersion> = this.store.select(ScopeOverviewSelectors.selectCurrentScope)
  cdnConfig$: Observable<CdnConfig | undefined> = this.store.select(BootstrapSelectors.selectCdnConfig)
  availableRoles$: Observable<FindReplaceRole[]> = this.store.select(ScopingSelectors.selectAvailableRoles)
  findReplaceBreadcrumbs$: Observable<any> = this.store.select(ScopingSelectors.selectFindReplaceBreadcrumbs)
  isFindReplaceLoading$: Observable<boolean> = this.store.select(ScopeOverviewSelectors.selectCurrentScopeLoading)

  availableComponents$: Observable<any> = this.store.select(ScopingSelectors.selectAvailableComponents)
  availableDeliverables$: Observable<any> = this.store.select(ScopingSelectors.selectAvailableDeliverables)
  availableDepartments$: Observable<any> = this.store.select(ScopingSelectors.selectAvailableDepartments)

  replaceSummarySuccess$: Observable<boolean> = this.store.select(ScopingSelectors.selectReplaceSummarySuccess)
  replaceSummaryResponse$: Observable<any> = this.store.select(ScopingSelectors.selectReplaceRolesResponse)
  revertSummaryResponse$: Observable<any> = this.store.select(ScopingSelectors.selectRevertRolesResponse)
  revertSummarySuccess$: Observable<any> = this.store.select(ScopingSelectors.selectRevertSummarySuccess)

  rateCard$: Observable<any> = this.store.select(ScopingSelectors.selectRateCard)

  scopeOverviewFilters$: Observable<ScopeOverviewFilter> = this.store.select(
    ScopingSelectors.selectScopeOverviewFilters
  )
  scopeColumnFilterPreferences$: Observable<TableColumnPreferences> = this.store.select(
    ScopingSelectors.selectScopeOverviewColumns
  )

  scopeRetainedHoursStats$: Observable<ScopeRetainedHoursStats> = this.store.select(
    ScopeOverviewSelectors.selectScopeRetainedHourStats
  )

  searchedComponents$: Observable<TypedFilterValue[]> = this.store.select(
    ScopeOverviewSelectors.selectSearchedComponentsForScope
  )
  searchedNonDistinctComponents$: Observable<ScopeComponent[]> = this.store.select(
    ScopeOverviewSelectors.selectSearchedNonDistinctComponentsForScope
  )
  searchedDepartments$: Observable<GenericFilterValue[]> = this.store.select(
    ScopeOverviewSelectors.selectSearchedDepartmentsForScope
  )
  searchedRoles$: Observable<GenericFilterValue[]> = this.store.select(
    ScopeOverviewSelectors.selectSearchedRolesForScope
  )

  filteredSectionsSource = new BehaviorSubject<ScopeSection[]>([])
  filteredSections$: Observable<ScopeSection[]> = this.filteredSectionsSource.asObservable()
  filteredSections?: ScopeSection[]

  filteredDepartmentsSource = new BehaviorSubject<Department[]>([])
  filteredDepartments$: Observable<Department[]> = this.filteredDepartmentsSource.asObservable()

  filteredScopeFeesSource = new BehaviorSubject<any[]>([])
  filteredScopeFees$: Observable<any[]> = this.filteredScopeFeesSource.asObservable()
  filteredScopeFees?: any[]

  filteredComponentsSource = new BehaviorSubject<ScopeComponent[]>([])
  filteredComponents$: Observable<ScopeComponent[]> = this.filteredComponentsSource.asObservable()
  filteredComponents?: ScopeComponent[]
  filteredComponentFeesSource = new BehaviorSubject<any[]>([])
  filteredComponentFees$: Observable<any[]> = this.filteredComponentFeesSource.asObservable()
  filteredComponentFees?: any[]
  filteredComponentTPCsSource = new BehaviorSubject<ThirdPartyCost[]>([])
  filteredComponentTPCs$: Observable<ThirdPartyCost[]> = this.filteredComponentTPCsSource.asObservable()

  filteredDeliverables$: Observable<Deliverable[]> = this.store.select(
    ScopeOverviewSelectors.selectFilteredDeliverables
  )
  filteredDeliverableFeesSource = new BehaviorSubject<any[]>([])
  filteredDeliverableFees$: Observable<any[]> = this.filteredDeliverableFeesSource.asObservable()
  filteredDeliverableFees?: any[]
  filteredDeliverableTPCsSource = new BehaviorSubject<ThirdPartyCost[]>([])
  filteredDeliverableTPCs$: Observable<ThirdPartyCost[]> = this.filteredDeliverableTPCsSource.asObservable()
  filteredDeliverableSectionTPCsSource = new BehaviorSubject<ThirdPartyCost[]>([])
  filteredDeliverableSectionTPCs$: Observable<ThirdPartyCost[]> =
    this.filteredDeliverableSectionTPCsSource.asObservable()

  filteredRolesSource = new BehaviorSubject<Role[]>([])
  filteredRoles$: Observable<any> = this.filteredRolesSource.asObservable()
  filteredDepartmentRoles$: Observable<Role[]> = this.store.select(ScopingSelectors.selectFilteredRoles)

  sortedDefaultSectionDeliverables$: Observable<Deliverable[]>

  currentScope: ScopeVersion
  cdnConfig: CdnConfig
  companyIntegrations: any = []
  breadcrumbs!: any
  showFilterView = false
  showUseRetainedTeamToggle = false

  private currentRatecard: RatecardVersion
  private updateSubject = new Subject<{ event: ThirdPartyCost }>()
  private completeReviewDialogConfig: MatDialogRef<ReviewSubmitModalComponent, any>
  private createDeliverableDialog: MatDialogRef<CreateDeliverableModalComponent, any>

  get currentTab(): string {
    return this.scopeOverviewNavigationService.currentTab
  }
  get loggedInUser(): User {
    return this.authService.loggedInUser
  }

  constructor(
    private store: Store,
    private scopeOverviewNavigationService: ScopeOverviewNavigationService,
    private authService: AuthService,
    private scopeOverviewService: ScopeOverviewService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private snackbarService: SnackbarService,
    private traffickingService: ScopeTraffickingService,
    private router: Router,
    private scopingService: MyScopesService,
    private approvalsService: ApprovalsService,
    private scopeTeamService: ScopeTeamService,
    private cdr: ChangeDetectorRef,
    private userSettingsService: UserSettingsService,
    protected findReplaceService: FindReplaceService,
    private dialogEventsService: DialogEventsService,
    protected scopeApprovalFlowService: ApprovalFlowService
  ) {
    this.destroy$ = untilDestroyed()
  }

  ngOnInit(): void {
    this.setupDebouncedUpdate()

    this.currentScope$
      .pipe(this.destroy$())
      .subscribe((currentScope: ScopeVersion) => (this.currentScope = currentScope))

    this.getCurrentRatecard()
    this.getRetainedTeamStats()

    this.cdnConfig$.pipe(this.destroy$()).subscribe((config: CdnConfig) => (this.cdnConfig = config))

    this.userSettingsService.getCompanyIntegrations().subscribe((response) => {
      this.companyIntegrations = response
    })

    this.sortedDefaultSectionDeliverables$ = this.store.pipe(
      select(ScopeOverviewSelectors.selectDisplayedDeliverables),
      map((deliverables) => deliverables.filter((d) => !d.section).sort((a, b) => a.order - b.order))
    )

    this.onFindReplaceSuccess()
    this.openDeliverableAcceptRejectModals()
  }

  initScopeRetainedStats(currentScope: ScopeVersion): void {
    if (currentScope.scopeOfWorkVersion && currentScope.useRetainedHours) {
      this.store.dispatch(
        ScopeOverviewActions.getScopeRetainedStats({
          sowId: currentScope.scopeOfWorkVersion.identity.id,
          scopeId: currentScope.id,
        })
      )
    }

    if (currentScope.scopeOfWorkVersion) {
      this.store.dispatch(
        ScopeOverviewActions.getSowRetainedStats({ folderId: currentScope.scopeOfWorkVersion.identity.id })
      )
    }
  }

  onFindReplaceSuccess() {
    this.replaceSummarySuccess$.pipe(filter((success) => success == true)).subscribe(() => {
      this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
    })

    this.revertSummarySuccess$.pipe(filter((success) => success == true)).subscribe(() => {
      this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
    })
  }

  openDeliverableAcceptRejectModals() {
    this.dialogEventsService.dialogEventEmitter
      .pipe(this.destroy$())
      .subscribe((data: { key: { key: string; optionalData: { text: string; hasOverride: boolean } } }) => {
        const {
          key: { key, optionalData },
        } = data

        if (key === 'openAcceptDeliverableDialog' || key === 'openRejectDeliverableDialog') {
          this.openAcceptDeliverablesModal(key)
        }

        if (key === 'openCompleteReviewModal') {
          this.submitScopeReview(optionalData.text, optionalData.hasOverride)
        }

        if (key === 'exportScopeToExcel') {
          this.openExcelExportModal()
        }

        if (key === 'exportTrafficThroughAgencyTrafficSystem') {
          this.exportTrafficThroughAgencyTrafficSystem()
        }

        if (key === 'exportTrafficThroughWorkato') {
          this.exportTrafficThroughWorkato()
        }
      })
  }

  openExcelExportModal() {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((currentScope) => !!currentScope)
      )
      .subscribe((currentScope) => {
        const dialogConfig: ExportModalConfig = {
          title: 'Export as Excel Output',
          body: 'Please select which excel document file you want to Output for scope',
          currentScope: currentScope,
          currentUser: this.loggedInUser,
          limitModalWidth: true,
          scopeId: this.currentScope.identity.id,
        }

        this.dialog.open(ExportExcelModal, { data: dialogConfig })
      })
  }

  fetchDeliverable(deliverableId: number) {
    this.store.dispatch(
      ScopePaginatedOverviewActions.getScopeDeliverable({
        scopeId: this.currentScope.identity.id,
        deliverableId,
      })
    )
  }

  removeDuplicates = (arr: any[]): any[] => this.findReplaceService.removeDuplicates(arr)

  onSearch(searchData: FindRolesSearchParams) {
    this.findReplaceService.onSearch(this.currentScope.identity.id, searchData)
  }

  onSearchReplaceRole(searchString: string) {
    this.findReplaceService.onSearchReplaceRole(searchString, this.currentRatecard)
  }

  onSelectionChange(selectionEvent: any) {
    this.findReplaceService.onSelectionChange(selectionEvent)
    this.breadcrumbs = generateBreadCrumbs(this.findReplaceService.selectedRole)
  }

  onRevert(revertId: number) {
    this.store.dispatch(ScopeActions.revertReplacementRoles({ scopeId: this.currentScope.identity.id, revertId }))
  }

  submitReplacedRoles(event: { roles: any; roleId: number; replaceRoleId: number }) {
    this.store.dispatch(
      ScopeActions.replaceRoles({
        selectedRoleId: event.roleId,
        selectedRoleReplaceRoleId: event.replaceRoleId,
        roles: event.roles,
        scopeId: this.currentScope.identity.id,
      })
    )
  }

  // Scope Overview Filters
  resetFilteredItems() {
    this.filteredSections = []
    this.filteredSectionsSource.next(this.filteredSections)
    this.store.dispatch(ScopeOverviewActions.filterDeliverablesSuccess({ filteredDeliverables: [] }))
    this.filteredComponents = []
    this.filteredComponentsSource.next(this.filteredComponents)
    this.filteredDepartmentsSource.next([])
    this.filteredRolesSource.next([])
    this.filteredScopeFees = []
    this.filteredScopeFeesSource.next(this.filteredScopeFees)
    this.filteredDeliverableFees = []
    this.filteredDeliverableFeesSource.next(this.filteredDeliverableFees)
    this.filteredComponentFees = []
    this.filteredComponentFeesSource.next(this.filteredComponentFees)
    this.filteredDeliverableTPCsSource.next([])
    this.filteredDeliverableSectionTPCsSource.next([])
    this.filteredComponentTPCsSource.next([])
  }

  onFilterScopes(filters: ScopeOverviewFilter, currentScope: ScopeVersion) {
    this.resetFilteredItems()

    if (isEqual(filters, ScopeOverviewFilterDefaults)) {
      this.showFilterView = false
      return
    }

    this.showFilterView = true
    const hasBudgetFilters = filters.budget?.minimum || filters.budget?.maximum
    const hasTpcFilters = filters.thirdPartyCost?.minimum || filters.thirdPartyCost?.maximum
    const hasObjectFilters =
      filters.stages?.length ||
      filters.deliverables?.length ||
      filters.components?.components?.length ||
      filters.components?.complexity ||
      hasTpcFilters ||
      filters.departments?.length ||
      filters.roles?.length ||
      filters.disciplines?.length ||
      hasBudgetFilters
    const hasProfitFilters =
      filters.margin?.minimum || filters.margin?.maximum || filters.profit?.minimum || filters.profit?.maximum
    const hasMarkupFilters = filters.markUp?.minimum || filters.markUp?.maximum

    if (!hasObjectFilters || filters.stages?.length) {
      this.scopeOverviewService
        .filterScopeSections(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((sections) => {
          sections.forEach((s) => {
            s.deliverables = currentScope.deliverables.filter((d) => d.section?.id === s.id)
          })
          this.filteredSections = sections
          this.filteredSectionsSource.next(this.filteredSections)
        })
    } else {
      this.filteredSections = []
      this.filteredSectionsSource.next(this.filteredSections)
    }
    if (!hasObjectFilters || filters.deliverables?.length || filters.disciplines?.length || hasBudgetFilters) {
      this.store.dispatch(
        ScopeOverviewActions.filterDeliverables({
          scopeId: this.currentScope.identity.id,
          versionId: currentScope.version,
          filters,
        })
      )
    } else {
      this.store.dispatch(ScopeOverviewActions.filterDeliverablesSuccess({ filteredDeliverables: [] }))
    }
    if (!hasObjectFilters || filters.components?.components?.length || filters.components?.complexity) {
      this.scopeOverviewService
        .filterScopeComponents(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((components) => {
          this.filteredComponents = components
          this.filteredComponentsSource.next(this.filteredComponents)
        })
    } else {
      this.filteredComponents = []
      this.filteredComponentsSource.next(this.filteredComponents)
    }
    if ((!hasObjectFilters || filters.departments?.length) && !hasProfitFilters && !hasMarkupFilters) {
      this.scopeOverviewService
        .filterScopeDepartments(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((departments) => {
          this.filteredDepartmentsSource.next(departments)
        })
    } else {
      this.filteredDepartmentsSource.next([])
    }
    if ((!hasObjectFilters || filters.roles?.length) && !hasProfitFilters && !hasMarkupFilters) {
      this.scopeOverviewService
        .filterScopeRoles(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((roles) => {
          this.filteredRolesSource.next(roles)
        })
    } else {
      this.filteredRolesSource.next([])
    }
    // Only filter fees by name
    if (!hasObjectFilters && !hasProfitFilters && !hasMarkupFilters && filters.name.length) {
      this.scopeOverviewService
        .filterScopeFees(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((fees) => {
          this.filteredScopeFees = fees
          this.filteredScopeFeesSource.next(this.filteredScopeFees)
        })
      this.scopeOverviewService
        .filterScopeDeliverableFees(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((fees) => {
          this.filteredDeliverableFees = fees
          this.filteredDeliverableFeesSource.next(this.filteredDeliverableFees)
        })
      this.scopeOverviewService
        .filterScopeComponentFees(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((fees) => {
          this.filteredComponentFees = fees
          this.filteredComponentFeesSource.next(this.filteredComponentFees)
        })
    } else {
      this.filteredScopeFees = []
      this.filteredScopeFeesSource.next(this.filteredScopeFees)
      this.filteredDeliverableFees = []
      this.filteredDeliverableFeesSource.next(this.filteredDeliverableFees)
      this.filteredComponentFees = []
      this.filteredComponentFeesSource.next(this.filteredComponentFees)
    }
    if ((!hasObjectFilters && !hasProfitFilters) || hasTpcFilters) {
      this.scopeOverviewService
        .filterScopeDeliverableTPCs(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((thirdPartyCosts) => {
          this.filteredDeliverableTPCsSource.next(thirdPartyCosts)
        })
      this.scopeOverviewService
        .filterScopeDeliverableSectionTPCs(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((thirdPartyCosts) => {
          this.filteredDeliverableSectionTPCsSource.next(thirdPartyCosts)
        })
      this.scopeOverviewService
        .filterScopeComponentTPCs(this.currentScope.identity.id, currentScope.version, filters)
        .subscribe((thirdPartyCosts) => {
          this.filteredComponentTPCsSource.next(thirdPartyCosts)
        })
    } else {
      this.filteredDeliverableTPCsSource.next([])
      this.filteredDeliverableSectionTPCsSource.next([])
      this.filteredComponentTPCsSource.next([])
    }
  }

  deleteFee(fee: FeeEvent) {
    const body = `Are you sure you want to delete "${fee.feeItem.feeItem.name}"?`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete "${fee.feeItem.feeItem.name}"`,
        body,
        'Delete',
        undefined,
        () => {
          dialog.close()
          if (fee.componentId) return this.deleteComponentFee(fee)
          if (fee.deliverableId) return this.deleteDeliverableFee(fee)
          if (fee.sectionId) return this.deleteSectionFee(fee)

          this.store.dispatch(
            ScopeOverviewActions.deleteScopeFeeItem({
              scopeId: this.currentScope.identity.id,
              feeItemId: fee.feeItem.id,
            })
          )
          if (this.filteredScopeFees) {
            this.filteredScopeFees = this.filteredScopeFees.filter((s) => s.id != fee.feeItem.id)
            this.filteredScopeFeesSource.next(this.filteredScopeFees)
          }
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  deleteComponentFee(fee: FeeEvent) {
    this.scopeOverviewService
      .deleteComponentFeeItem(this.currentScope.identity.id, fee.deliverableId, fee.componentId, fee.feeItem.id)
      .subscribe({
        next: () => {
          this.store
            .select(ScopeOverviewSelectors.selectDeliverable(fee.deliverableId))
            .pipe(take(1))
            .subscribe((deliverable: Deliverable) => {
              if (!deliverable.componentsInitialised) this.fetchDeliverable(fee.deliverableId)
            })
          this.store.dispatch(
            ScopeOverviewActions.deleteComponentFeeSuccess({
              deliverableId: fee.deliverableId,
              componentId: fee.componentId,
              feeItemId: fee.feeItem.id,
            })
          )
          if (this.filteredComponentFees) {
            this.filteredComponentFees = this.filteredComponentFees.filter((s) => s.id != fee.feeItem.id)
            this.filteredComponentFeesSource.next(this.filteredComponentFees)
          }
        },
        error: (error) => {
          this.snackbarService.showErrorSnackbar(
            `An error occurred during deletion of ${this.lang.get('component|l')} fee`
          )
          return of(ScopeOverviewActions.deleteComponentFeeFail({ error }))
        },
      })
  }

  deleteDeliverableFee(fee: FeeEvent) {
    this.scopeOverviewService
      .deleteDeliverableFeeItem(this.currentScope.identity.id, fee.deliverableId, fee.feeItem.id)
      .subscribe({
        next: () => {
          this.store
            .select(ScopeOverviewSelectors.selectDeliverable(fee.deliverableId))
            .pipe(take(1))
            .subscribe((deliverable: Deliverable) => {
              if (!deliverable.componentsInitialised) this.fetchDeliverable(fee.deliverableId)
            })
          this.store.dispatch(
            ScopeOverviewActions.deleteDeliverableFeeItemSuccess({
              deliverableId: fee.deliverableId,
              feeItemId: fee.feeItem.id,
            })
          )
          if (this.filteredDeliverableFees) {
            this.filteredDeliverableFees = this.filteredDeliverableFees.filter((s) => s.id != fee.feeItem.id)
            this.filteredDeliverableFeesSource.next(this.filteredDeliverableFees)
          }
        },
        error: (error) => {
          this.snackbarService.showErrorSnackbar(
            `An error occurred during deletion of ${this.lang.get('deliverable|l')} fee`
          )
          return of(ScopeOverviewActions.deleteDeliverableFeeItemFail({ error }))
        },
      })
  }

  deleteSectionFee(fee: FeeEvent) {
    this.store.dispatch(
      ScopeOverviewActions.deleteSectionFeeItem({
        scopeId: this.currentScope.identity.id,
        sectionId: fee.sectionId,
        feeItemId: fee.feeItem.id,
      })
    )
  }

  addScopeSection(currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: `Add new ${this.lang.get('stage')}`,
      confirmText: 'Complete',
      confirmCallback: (form: FormControl) => {
        dialog.close()
        const { name, description, duration } = form.value
        const section = plainToInstance(ScopeSection, {
          name,
          description,
          startDate: duration?.startDate,
          endDate: duration?.endDate,
        })
        this.store.dispatch(ScopeOverviewActions.addScopeSection({ scopeId: this.currentScope.identity.id, section }))
      },
      isFormValid: (form: FormControl) => form.value.name,
      inputs: [
        { name: 'name', control: new FormControl(''), type: 'text', label: 'Name' },
        { name: 'description', control: new FormControl(''), type: 'description' },
        {
          name: 'duration',
          control: new FormControl({ startDate: currentScope.startDate, endDate: currentScope.endDate }),
          type: 'duration',
          minDate: currentScope.startDate,
          maxDate: currentScope.endDate,
        },
      ],
      isLoading$: this.store.select(ScopeOverviewSelectors.selectAddScopeSectionLoading),
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  deleteScopeSection(section: ScopeSection, scope: ScopeVersion) {
    let body = `Are you sure you want to delete ` + section.name
    if (scope.deliverables.find((d) => d.section?.id === section.id)) {
      body += ` and ${scope.isScopeByRole() ? 'tasks' : 'deliverables'}  inside of it`
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete ` + section.name,
        body + `?`,
        'Delete',
        undefined,
        () => {
          dialog.close()
          this.store.dispatch(
            ScopeOverviewActions.deleteScopeSection({ scopeId: this.currentScope.identity.id, sectionId: section.id })
          )
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  archiveScope(currentScope: ScopeVersion) {
    const body = `Archiving the scope will make it read-only. No further changes will be possible`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Archive "` + currentScope.name + `"`,
        body,
        'Archive',
        undefined,
        () => {
          dialog.close()
          this.store.dispatch(ScopeOverviewActions.archiveScope({ scopeId: this.currentScope.identity.id }))
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  unarchiveScope(currentScope: ScopeVersion) {
    const body = `Are you sure you want to unarchive the ${this.lang.get('scope')}`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Unarchive "` + currentScope.name + `"`,
        body,
        'Unarchive',
        undefined,
        () => {
          dialog.close()
          this.store.dispatch(ScopeOverviewActions.unarchiveScope({ scopeId: this.currentScope.identity.id }))
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  createDeliverable(currentScope: ScopeVersion, isFixedFee = false) {
    this.createDeliverableDialog = this.dialog.open(CreateDeliverableModalComponent)
    this.store.dispatch(ScopeOverviewActions.setDialogId({ id: this.createDeliverableDialog.id }))

    this.createDeliverableDialog.componentInstance.cdnConfig = this.cdnConfig!
    this.createDeliverableDialog.componentInstance.isFixedFee = isFixedFee
    this.createDeliverableDialog.componentInstance.currentScope = currentScope
    this.createDeliverableDialog.componentInstance.owner = this.loggedInUser
    this.createDeliverableDialog.componentInstance.confirmCallback.subscribe((event: AddDeliverableEvent) => {
      this.createDeliverableDialog.componentInstance.isRateCardsLoading = true
      if (event.isNewCustomDeliverable) {
        this.addCustomDeliverable(event.form, event.selection, currentScope, event.quickAdd)
      } else {
        if (
          event.selection.deliverableType!.ratecardVersionId === currentScope.identity.rateCard.id ||
          event.selection.deliverableType.sourceType === 'SCOPE'
        ) {
          this.createDeliverableDialog.close()
          this.addExistingDeliverable(
            event.form,
            event.selection,
            currentScope.identity.rateCard.currencyCode,
            event.quickAdd
          )
        } else {
          const targetRatecard$ = this.scopeOverviewService.getScopeRatecardByVersion(
            currentScope.identity.id,
            currentScope.identity.rateCard.version
          )
          const sourceRatecard$ =
            event.selection.deliverableType.sourceType == 'LIBRARY'
              ? this.scopeOverviewService.getRatecardVersionForDeliverableEntry(
                  event.selection.deliverableType.parentId,
                  event.selection.deliverableType.id
                )
              : this.scopeOverviewService.getRatecardVersion(event.selection.deliverableType.ratecardVersionId)

          forkJoin([targetRatecard$, sourceRatecard$]).subscribe({
            next: ([targetRatecard, sourceRatecard]: [RatecardVersion, RatecardVersion]) => {
              this.scopeOverviewService
                .getDeliverableEntry(
                  event.selection.deliverableType.parentId,
                  event.selection.deliverableType.discipline.id,
                  targetRatecard.id
                )
                .subscribe({
                  next: (entry) => {
                    event.selection.deliverableType = plainToInstance(DeliverableType, {
                      ...event.selection.deliverableType,
                      id: entry.id,
                      ratecardVersionId: entry.rateCardVersion.id,
                    })
                    this.addExistingDeliverable(
                      event.form,
                      event.selection,
                      currentScope.identity.rateCard.currencyCode,
                      event.quickAdd
                    )
                  },
                  error: () => {
                    this.showRoleMappingDialogOrCreateDeliverable(
                      event,
                      targetRatecard,
                      sourceRatecard,
                      currentScope.identity.rateCard.currencyCode
                    )
                  },
                  complete: () => this.resetCreateDeliverableDialogValues(),
                })
            },
            error: (error) => {
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              this.resetCreateDeliverableDialogValues()
              return error
            },
          })
        }
      }
    })
    this.createDeliverableDialog.componentInstance.currencyCode = currentScope.identity.rateCard.currencyCode

    this.store.dispatch(ScopeOverviewActions.getAllRateCardsForScopeCreate())
    const showAllRateCards$ = new BehaviorSubject<boolean>(false)

    const currentRateCardIdentityId = currentScope.identity.rateCard.rateCardIdentity.id
    const currentRateCardId = currentScope.identity.rateCard.id

    const rateCardVersionIdsSet$ = this.store.select(ScopeOverviewSelectors.selectAllRateCards).pipe(
      map((allRateCards) => {
        if (!allRateCards?.length) {
          return new Set([currentRateCardId])
        }
        const versionsInSameFamily = allRateCards
          .filter((rc) => rc.rateCardIdentity.id === currentRateCardIdentityId)
          .map((rc) => rc.id)

        return new Set(versionsInSameFamily)
      }),
      startWith(new Set([currentRateCardId])),
      shareReplay(1)
    )

    this.createDeliverableDialog.componentInstance.availableDeliverables$ = combineLatest([
      this.store.select(
        ScopeOverviewSelectors.selectAvailableDeliverableTypes(currentScope.identity.rateCard.currencyCode)
      ),
      showAllRateCards$,
      rateCardVersionIdsSet$,
    ]).pipe(
      map(([deliverables, showAll, allowedRateCardIds]) => {
        if (showAll) {
          return deliverables
        }

        return deliverables.filter((deliverable: DeliverableType) => {
          const isFromLibrary = deliverable.sourceType === 'LIBRARY'
          const hasRateCard = !!deliverable.ratecardVersionId
          const isFromCurrentRateCardFamily = allowedRateCardIds.has(deliverable.ratecardVersionId)

          return isFromLibrary && hasRateCard && isFromCurrentRateCardFamily
        })
      })
    )

    this.createDeliverableDialog.componentInstance.availableOwners$ = this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(map((scope) => scope.identity.team.collaborators.map((c) => c.user)))

    this.createDeliverableDialog.componentInstance.onShowAllRateCardsChanged.subscribe((showAll: boolean) => {
      showAllRateCards$.next(showAll)
    })

    this.createDeliverableDialog.componentInstance.onSearchDeliverables.subscribe((searchText: string) => {
      if (isFixedFee) {
        this.store.dispatch(
          ScopeOverviewActions.deliverableTypeSearch({
            fixedOnly: true,
            searchQuery: searchText,
            rateCardVersionId: currentScope.identity.rateCard.id,
            language: currentScope.identity.language,
          })
        )
      } else if (searchText && searchText.length) {
        this.store.dispatch(
          ScopeOverviewActions.deliverableTypeSearch({
            searchQuery: searchText,
            rateCardVersionId: currentScope.identity.rateCard.id,
            language: currentScope.identity.language,
          })
        )
      } else {
        this.store.dispatch(
          ScopeOverviewActions.getAvailableScopeDeliverableTypes({ mergeLibraryDeliverableEntryStrategy: 'DISCIPLINE' })
        )
      }
    })
    this.createDeliverableDialog.componentInstance.onInsertWithRemapRolesSuccess.subscribe((deliverableId: number) => {
      this.router.navigateByUrl(`/scopes/scope-overview/${this.currentScope.identity.id}/deliverable/${deliverableId}`)
      this.createDeliverableDialog.close()
    })
  }

  addCustomDeliverable(
    form: FormControl,
    selection: SelectedDeliverable,
    currentScope: ScopeVersion,
    quickAdd: boolean
  ) {
    const { name, libraryItemType, budget, discipline, owner } = form.value
    const deliverable: any = {
      name,
      description: selection.description,
      budget: new Money(budget || 0, currentScope.identity.rateCard.currencyCode),
      internalNote: '',
      language: currentScope.identity.language,
      libraryItemType,
      fixedPricing: libraryItemType == LibraryTemplateType.FIXED_COST,
      itemType: LibraryItemType.DELIVERABLE,
    }

    this.store.dispatch(
      ScopeOverviewActions.createLibraryDeliverableTemplate({ scopeId: this.currentScope.identity.id, deliverable })
    )

    return this.store
      .select(ScopeOverviewSelectors.selectDeliverableLibraryTemplate)
      .pipe(
        filter((template) => template != undefined && template.name == deliverable.name),
        take(1),
        switchMap((template) => {
          const entry: any = {
            discipline,
            rateCardVersion: currentScope.identity.rateCard,
            description: selection.description,
            fixedPricing: deliverable.fixedPricing,
          }
          if (deliverable.fixedPricing) entry.fixedCostEditable = true

          this.store.dispatch(ScopeOverviewActions.createLibraryDeliverableEntry({ templateId: template.id, entry }))

          return this.store.select(ScopeOverviewSelectors.selectDeliverableLibraryEntry).pipe(
            filter((updateEntry) => updateEntry != undefined && updateEntry.name == deliverable.name),
            take(1),
            map((updateEntry) => ({ updateEntry, template }))
          )
        })
      )
      .subscribe(({ updateEntry, template }) => {
        const searchResult = {
          id: updateEntry.id,
          name: template.name,
          description: template.description,
          isTrialRestricted: false,
          sourceType: 'LIBRARY',
        }
        const scopeDeliverable = plainToInstance(Deliverable, {
          source: searchResult,
          sourceType: searchResult.sourceType,
          owner: owner ? owner : (this.loggedInUser as User),
          name: deliverable.name,
          description: searchResult.description,
          fixedPricing: deliverable.fixedPricing,
          startDate: selection.startDate,
          endDate: selection.endDate,
          trafficSystemEntityMetadata: selection.trafficSystemEntityMetadata,
        })

        this.resetCreateDeliverableDialogValues()

        this.store.dispatch(
          ScopeOverviewActions.addScopeDeliverable({
            scopeId: this.currentScope.identity.id,
            deliverable: scopeDeliverable,
            editLibDelEntryId: updateEntry.id,
            quickAdd,
          })
        )
      })
  }

  addExistingDeliverable(form: FormControl, selection: SelectedDeliverable, currencyUnit: string, quickAdd: boolean) {
    const { name, budget } = form.value
    const deliverable = plainToInstance(Deliverable, {
      source: selection.deliverableType,
      sourceType: selection.deliverableType.sourceType,
      owner: this.loggedInUser as User,
      name: name ? name : selection.deliverableType.name,
      description: selection.description || selection.deliverableType.description,
      budget: new Money(budget || 0, currencyUnit),
      startDate: selection.startDate,
      endDate: selection.endDate,
      discipline: selection.deliverableType.discipline,
    })

    this.resetCreateDeliverableDialogValues()

    this.store.dispatch(
      ScopeOverviewActions.addScopeDeliverable({ scopeId: this.currentScope.identity.id, deliverable, quickAdd })
    )
  }

  resetCreateDeliverableDialogValues() {
    if (this.createDeliverableDialog?.componentInstance)
      this.createDeliverableDialog.componentInstance.isRateCardsLoading = false
  }

  showRoleMappingDialogOrCreateDeliverable = (
    event: AddDeliverableEvent,
    targetRatecard: RatecardVersion,
    sourceRatecard: RatecardVersion,
    currencyCode: string
  ) => {
    this.prepareMapping(
      event.selection.deliverableType.id,
      targetRatecard,
      (
        usedRoles: Role[],
        mappingRoles: Role[],
        targetRoles: Role[],
        mappingRoleKeys: Record<number, number>,
        deliverableEntry: any,
        showAgencyRates: boolean,
        distinctRolesMap: any
      ) => {
        if (usedRoles.length === mappingRoles.length) {
          this.scopeOverviewService
            .duplicateDeliverableEntry(
              deliverableEntry.deliverable.id,
              deliverableEntry.id,
              deliverableEntry.discipline,
              targetRatecard.id,
              mappingRoleKeys,
              true
            )
            .subscribe({
              next: (duplicatedEntry) => {
                this.createDeliverableDialog.close()
                event.selection.deliverableType = plainToInstance(DeliverableType, {
                  ...event.selection.deliverableType,
                  id: duplicatedEntry.id,
                  ratecardVersionId: duplicatedEntry.rateCardVersion.id,
                })
                this.addExistingDeliverable(event.form, event.selection, currencyCode, event.quickAdd)
              },
              error: (error) => {
                this.snackbarService.showDefaultErrorSnackbar()
                console.error(error)
                return error
              },
            })
        } else {
          this.createDeliverableDialog.componentInstance.nextModalStep = true
          this.createDeliverableDialog.componentInstance.mappingRoles = mappingRoles
          this.createDeliverableDialog.componentInstance.targetRoles = [{ name: 'Unassigned', id: null }].concat(
            targetRoles
          )
          this.createDeliverableDialog.componentInstance.sourceRatecard = sourceRatecard
          this.createDeliverableDialog.componentInstance.targetRatecard = targetRatecard
          this.createDeliverableDialog.componentInstance.showAgencyRates = showAgencyRates
          this.createDeliverableDialog.componentInstance.deliverableEntry = deliverableEntry
          this.createDeliverableDialog.componentInstance.distinctRolesMap = distinctRolesMap
        }
      }
    )
  }

  prepareMapping = (
    deliverableId: number,
    targetRatecard: RatecardVersion,
    callback: (
      usedRoles: Role[],
      mappingRoles: Role[],
      targetRoles: Role[],
      mappingRoleKeys: Record<number, number>,
      deliverableEntry: any,
      showAgencyRates: boolean,
      distinctRolesMap: any
    ) => void
  ) => {
    const distinctRolesMap = {}
    let mappingRoles = []
    let targetRoles = []
    const usedRoles = []
    const mappingRoleKeys: Record<number, number> = {}
    if (deliverableId)
      this.scopeOverviewService.getLibraryDeliverableEntry(deliverableId).subscribe({
        next: (deliverableEntry) => {
          const showAgencyRates = this.loggedInUser.company.id == deliverableEntry.createdBy.company.id
          const targetDepartments = targetRatecard.departments
          if (targetDepartments) {
            const targetDepartmentsRolesByName = {}

            targetDepartments.forEach(function (targetDepartment) {
              const roles = targetDepartment.roles
              roles.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0))
              roles.forEach(function (targetRole) {
                targetDepartmentsRolesByName[targetRole.name.toLowerCase()] = targetRole
              })
            })

            deliverableEntry.libraryComponents.forEach((libraryDeliverableEntryComponent) => {
              libraryDeliverableEntryComponent.roles.forEach(function (role) {
                const key = role.rateCardRole.name.toLowerCase()
                if (!distinctRolesMap[key]) {
                  const distinctRole: any = {
                    name: role.rateCardRole.name,
                    id: role.rateCardRole.id,
                    rate: role.rateCardRole.rate,
                  }
                  const targetRole = targetDepartmentsRolesByName[key]
                  if (targetRole) {
                    distinctRole.targetMappingRole = targetRole
                    usedRoles.push(targetRole.id)
                    mappingRoleKeys[role.rateCardRole.id] = targetRole.id
                  } else {
                    distinctRole.targetMappingRole = { name: 'Unassigned', id: null }
                  }
                  distinctRolesMap[key] = distinctRole
                }
              })
            })
            mappingRoles = Object.values(distinctRolesMap)
          }

          if (targetDepartments) {
            targetRoles = targetDepartments.flatMap(function (department) {
              return department.roles
            })
          }

          callback(
            usedRoles,
            mappingRoles,
            targetRoles,
            mappingRoleKeys,
            deliverableEntry,
            showAgencyRates,
            distinctRolesMap
          )
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  updateDeliverable({ deliverable }: UpdateDeliverablePayload) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeDeliverable({
        scopeId: this.currentScope.identity.id,
        deliverable,
        pessimistic: true,
      })
    )
  }

  moveDeliverable(payload: MoveDeliverablePayload) {
    this.store.dispatch(ScopePaginatedOverviewActions.moveDeliverable(payload))
  }

  updateComponent({ component }: UpdateComponentPayload) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeComponent({
        scopeId: this.currentScope.identity.id,
        deliverableId: component.deliverable.id,
        component: mapScopeComponentToApi(component),
        deliverable: null,
      })
    )
  }

  moveComponent(event: { component: ScopeComponent; deliverable: Deliverable }, currentScope: ScopeVersion) {
    const delId: number = event.component.deliverable.id
    currentScope.deliverables.map((d) => (d.id == event.deliverable.id ? event.deliverable : d))
    this.store.dispatch(
      ScopeOverviewActions.updateScopeComponent({
        scopeId: this.currentScope.identity.id,
        deliverableId: delId,
        component: event.component,
        deliverable: event.deliverable,
      })
    )
  }

  moveComponentOutsideDeliverable(component: ScopeComponent, currentScope: ScopeVersion) {
    this.store.dispatch(
      ScopeOverviewActions.moveComponentToDeliverable({
        scopeId: currentScope.identity.id,
        componentId: component.id,
        deliverableId: component.deliverable.id,
        name: component.updatedName,
        order: component.order,
      })
    )
  }

  duplicateDeliverable(deliverable: Deliverable) {
    this.store.dispatch(
      ScopeOverviewActions.duplicateScopeDeliverable({
        scopeId: this.currentScope.identity.id,
        deliverableId: deliverable.id,
      })
    )
  }

  private showDeleteModal(entity: any, onConfirm: () => void): void {
    const entityName = entity.name || 'this item'
    const body = `Are you sure you want to delete "${entityName}"?`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete "${entityName}"`,
        body,
        'Delete',
        undefined,
        () => {
          dialog.close()
          onConfirm()
          this.getLatestAndUpdateState()
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  deleteDeliverable(deliverable: Deliverable) {
    this.showDeleteModal(deliverable, () => {
      this.store.dispatch(
        ScopeOverviewActions.deleteDeliverable({
          scopeId: this.currentScope.identity.id,
          deliverableId: deliverable.id,
        })
      )
    })
  }

  deleteComponent(component: ScopeComponent) {
    this.showDeleteModal(component, () => {
      this.store.dispatch(
        ScopeOverviewActions.deleteComponent({
          scopeId: this.currentScope.identity.id,
          deliverableId: component.deliverable.id,
          componentId: component.id,
        })
      )
    })
  }

  addFee(currencyCode: string, element?: any) {
    const dialog = this.dialog.open(AddEditFeeModalComponent)
    this.store.dispatch(ScopeActions.getAllFeeItems())
    dialog.componentInstance.existingFees$ = this.store.select(ScopingSelectors.selectAllFeeItems)
    dialog.componentInstance.currencyCode = currencyCode
    dialog.componentInstance.modalTitle = `Add ${
      element instanceof ScopeSection
        ? this.lang.get('stage|l')
        : element instanceof Deliverable
          ? this.lang.get('deliverable|l')
          : element instanceof ScopeComponent
            ? this.lang.get('component|l')
            : this.lang.get('scope|l')
    } fee`

    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem }) => {
      dialog.close()
      this.submitFee(event.feeItem, element)
    })
  }

  addAndLinkFee(currentScope: ScopeVersion) {
    const dialog = this.dialog.open(AddEditFeeModalComponent)
    this.store.dispatch(ScopeActions.getAllFeeItems())
    dialog.componentInstance.existingFees$ = this.store.select(ScopingSelectors.selectAllFeeItems)
    dialog.componentInstance.currencyCode = currentScope.identity.rateCard.currencyCode
    dialog.componentInstance.linkFee = true
    dialog.componentInstance.currentUser = this.loggedInUser
    this.store.dispatch(
      ScopeOverviewActions.searchNonDistinctComponents({
        scopeId: this.currentScope.identity.id,
        versionId: currentScope.version,
        searchText: '',
      })
    )

    dialog.componentInstance.modalTitle = `Add fee`
    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem; element: any }) => {
      dialog.close()
      this.submitFee(event.feeItem, event.element)
    })
  }

  submitFee(feeItem: FeeItem, element: any) {
    if (element instanceof ScopeSection) {
      this.submitSectionFee(feeItem, element.id)
    } else if (element instanceof Deliverable) {
      this.submitDeliverableFee(feeItem, element.id)
    } else if (element instanceof ScopeComponent) {
      this.submitComponentFee(feeItem, element.deliverable.id, element.id)
    } else {
      this.submitScopeFee(feeItem)
    }
  }

  submitScopeFee(feeItem: FeeItem) {
    this.scopeOverviewService.createScopeFeeItem(this.currentScope.identity.id, feeItem).subscribe({
      next: (result) => {
        this.store.dispatch(ScopeOverviewActions.createScopeFeeItemSuccess({ feeItem: result }))
      },
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        console.error(error)
        return error
      },
    })
  }

  submitSectionFee(feeItem: FeeItem, sectionId: number) {
    this.scopeOverviewService.createSectionFeeItem(this.currentScope.identity.id, sectionId, feeItem).subscribe({
      next: (result) => {
        this.store.dispatch(ScopeOverviewActions.createSectionFeeItemSuccess({ sectionId, feeItem: result }))
      },
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        console.error(error)
        return error
      },
    })
  }

  submitDeliverableFee(feeItem: FeeItem, deliverableId: number) {
    this.scopeOverviewService
      .createDeliverableFeeItem(this.currentScope.identity.id, deliverableId, feeItem)
      .subscribe({
        next: (result) => {
          this.store
            .select(ScopeOverviewSelectors.selectDeliverable(deliverableId))
            .pipe(take(1))
            .subscribe((deliverable: Deliverable) => {
              if (!deliverable.componentsInitialised) {
                this.fetchDeliverable(deliverableId)
              }
            })
          this.store.dispatch(ScopeOverviewActions.createDeliverableFeeItemSuccess({ deliverableId, feeItem: result }))
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  submitComponentFee(feeItem: FeeItem, deliverableId: number, componentId: number) {
    this.scopeOverviewService
      .createComponentFeeItem(this.currentScope.identity.id, deliverableId, componentId, feeItem)
      .subscribe({
        next: (result) => {
          this.store
            .select(ScopeOverviewSelectors.selectDeliverable(deliverableId))
            .pipe(take(1))
            .subscribe((deliverable: Deliverable) => {
              if (!deliverable.componentsInitialised) {
                this.fetchDeliverable(deliverableId)
              }
            })
          this.store.dispatch(
            ScopeOverviewActions.createComponentFeeItemSuccess({ deliverableId, componentId, feeItem: result })
          )
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  editFee(
    event: { feeItem: any; sectionId?: number; deliverableId?: number; componentId?: number },
    currencyCode: string
  ) {
    const dialog = this.dialog.open(AddEditFeeModalComponent)
    this.store.dispatch(ScopeActions.getAllFeeItems())
    dialog.componentInstance.existingFees$ = this.store.select(ScopingSelectors.selectAllFeeItems)
    dialog.componentInstance.currencyCode = currencyCode

    if (event.componentId) {
      this.editComponentFee(dialog, event.feeItem.id, event.feeItem.feeItem, event.deliverableId, event.componentId)
    } else if (event.deliverableId) {
      this.editDeliverableFee(dialog, event.feeItem.id, event.feeItem.feeItem, event.deliverableId)
    } else if (event.sectionId) {
      this.editSectionFee(dialog, event.feeItem.id, event.feeItem.feeItem, event.sectionId)
    } else {
      this.editScopeFee(dialog, event.feeItem.id, event.feeItem.feeItem)
    }
  }

  editScopeFee(dialog: MatDialogRef<AddEditFeeModalComponent>, feeItemId: number, feeItem: any) {
    dialog.componentInstance.modalTitle = `Edit "${feeItem.name}" ${this.lang.get('scope|l')} fee`
    dialog.componentInstance.feeItem = feeItem
    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem; element: any }) => {
      dialog.close()
      this.scopeOverviewService.updateScopeFeeItem(this.currentScope.identity.id, feeItemId, event.feeItem).subscribe({
        next: (result) => {
          this.store.dispatch(ScopeOverviewActions.editScopeFeeItemSuccess({ feeItemId, feeItem: result }))
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
    })
  }

  editSectionFee(dialog: MatDialogRef<AddEditFeeModalComponent>, feeItemId: number, feeItem: any, sectionId: number) {
    dialog.componentInstance.modalTitle = `Edit "${feeItem.name}" ${this.lang.get('stage|l')} fee`
    dialog.componentInstance.feeItem = feeItem
    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem; element: any }) => {
      dialog.close()
      this.scopeOverviewService
        .updateSectionFeeItem(this.currentScope.identity.id, sectionId, feeItemId, event.feeItem)
        .subscribe({
          next: (result) => {
            this.store.dispatch(
              ScopeOverviewActions.editSectionFeeItemSuccess({ feeItemId, sectionId, feeItem: result })
            )
          },
          error: (error) => {
            this.snackbarService.showDefaultErrorSnackbar()
            console.error(error)
            return error
          },
        })
    })
  }

  editDeliverableFee(
    dialog: MatDialogRef<AddEditFeeModalComponent>,
    feeItemId: number,
    feeItem: any,
    deliverableId: number
  ) {
    dialog.componentInstance.modalTitle = `Edit "${feeItem.name}" ${this.lang.get('deliverable|l')} fee`
    dialog.componentInstance.feeItem = feeItem
    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem; element: any }) => {
      dialog.close()
      this.scopeOverviewService
        .updateDeliverableFeeItem(this.currentScope.identity.id, deliverableId, feeItemId, event.feeItem)
        .subscribe({
          next: (result) => {
            this.store
              .select(ScopeOverviewSelectors.selectDeliverable(deliverableId))
              .pipe(take(1))
              .subscribe((deliverable: Deliverable) => {
                if (!deliverable.componentsInitialised) {
                  this.fetchDeliverable(deliverableId)
                }
              })
            this.store.dispatch(
              ScopeOverviewActions.editDeliverableFeeItemSuccess({ feeItemId, deliverableId, feeItem: result })
            )
          },
          error: (error) => {
            this.snackbarService.showDefaultErrorSnackbar()
            console.error(error)
            return error
          },
        })
    })
  }

  editComponentFee(
    dialog: MatDialogRef<AddEditFeeModalComponent>,
    feeItemId: number,
    feeItem: any,
    deliverableId: number,
    componentId: number
  ) {
    dialog.componentInstance.modalTitle = `Edit "${feeItem.name}" ${this.lang.get('component|l')} fee`
    dialog.componentInstance.feeItem = feeItem
    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem; element: any }) => {
      dialog.close()
      this.scopeOverviewService
        .updateComponentFeeItem(this.currentScope.identity.id, deliverableId, componentId, feeItemId, event.feeItem)
        .subscribe({
          next: (result) => {
            this.store
              .select(ScopeOverviewSelectors.selectDeliverable(deliverableId))
              .pipe(take(1))
              .subscribe((deliverable: Deliverable) => {
                if (!deliverable.componentsInitialised) {
                  this.fetchDeliverable(deliverableId)
                }
              })
            this.store.dispatch(
              ScopeOverviewActions.editComponentFeeItemSuccess({
                feeItemId,
                deliverableId,
                componentId,
                feeItem: result,
              })
            )
          },
          error: (error) => {
            this.snackbarService.showDefaultErrorSnackbar()
            console.error(error)
            return error
          },
        })
    })
  }

  updateFee(event: FeeEvent) {
    this.scopeOverviewService.updateCompanyFeeItem(event.feeItem.feeItem).subscribe({
      next: (result: FeeItem) => {
        if (event.componentId) {
          this.store.dispatch(
            ScopeOverviewActions.editComponentFeeItemSuccess({
              feeItemId: event.feeItem.id,
              deliverableId: event.deliverableId,
              componentId: event.componentId,
              feeItem: { ...event.feeItem, feeItem: result },
            })
          )
        } else if (event.deliverableId) {
          this.store.dispatch(
            ScopeOverviewActions.editDeliverableFeeItemSuccess({
              feeItemId: event.feeItem.id,
              deliverableId: event.deliverableId,
              feeItem: { ...event.feeItem, feeItem: result },
            })
          )
        } else if (event.sectionId) {
          this.store.dispatch(
            ScopeOverviewActions.editSectionFeeItemSuccess({
              feeItemId: event.feeItem.id,
              sectionId: event.sectionId,
              feeItem: { ...event.feeItem, feeItem: result },
            })
          )
        } else {
          this.store.dispatch(
            ScopeOverviewActions.editScopeFeeItemSuccess({
              feeItemId: event.feeItem.id,
              feeItem: { ...event.feeItem, feeItem: result },
            })
          )
        }
      },
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        console.error(error)
        return error
      },
    })
  }

  deleteScope(currentScope: ScopeVersion) {
    const body = `Are you sure you want to delete this ${this.lang.get('scope')}?`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete "` + currentScope.name + `"`,
        body,
        'Delete',
        undefined,
        () => {
          dialog.close()
          this.scopingService.deleteScope(this.currentScope.identity.id).subscribe({
            next: () => {
              this.router.navigateByUrl('/scopes')
            },
            error: (error: any) => {
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  onSearchComponents($event: string, currentScope: ScopeVersion) {
    this.store.dispatch(
      ScopeOverviewActions.searchComponentsForScope({
        scopeId: this.currentScope.identity.id,
        versionId: currentScope.version,
        searchText: $event,
      })
    )
  }

  onSearchDepartments($event: string, currentScope: ScopeVersion) {
    this.store.dispatch(
      ScopeOverviewActions.searchDepartmentsForScope({
        scopeId: this.currentScope.identity.id,
        versionId: currentScope.version,
        searchText: $event,
      })
    )
  }

  onSearchRoles($event: string, currentScope: ScopeVersion) {
    this.store.dispatch(
      ScopeOverviewActions.searchRolesForScope({
        scopeId: this.currentScope.identity.id,
        versionId: currentScope.version,
        searchText: $event,
      })
    )
  }

  acceptDeliverable(deliverable: Deliverable) {
    const data: ModalConfig = {
      title: 'Accept Deliverable Review',
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Accept Deliverable',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const approvalText: string = form.get('comment').value
        this.approvalsService
          .acceptDeliverableReview(this.currentScope.identity.id, approvalText, deliverable.id)
          .pipe(this.destroy$())
          .subscribe({
            next: () => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.stateChecks.deliverableStatus.deliverableAccepted = true
              if (this.isDeliverable(deliverable)) {
                this.scopeApprovalFlowService.stateChecks.deliverableStatus.deliverableInReview = deliverable.id
              }
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  rejectDeliverable(deliverable: Deliverable) {
    const data: ModalConfig = {
      title: 'Reject Deliverable Review',
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Reject Deliverable',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const approvalText: string = form.get('comment').value
        this.approvalsService
          .rejectDeliverableReview(this.currentScope.identity.id, approvalText, deliverable.id)
          .pipe(this.destroy$())
          .subscribe({
            next: () => {
              this.setApprovalLoading(false)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      cancelCallback: undefined,
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isMemberModal: undefined,
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  tradeDeliverableModal(deliverable: Deliverable, currentScope: ScopeVersion) {
    const dialogConfig: TrackTradeModalConfig = {
      title: `Trade ${this.lang.get('deliverable')}`,
      deliverable,
      deliverables$: this.store.select(
        ScopeOverviewSelectors.selectAvailableDeliverableTypes(currentScope.identity.rateCard.currencyCode)
      ),
      currentScope: currentScope,
      currencyCode: currentScope.identity.rateCard.currencyCode,
      onSearch: (searchText: string) => {
        if (searchText && searchText.length) {
          this.store.dispatch(
            ScopeOverviewActions.deliverableTypeSearch({
              searchQuery: searchText,
              rateCardVersionId: currentScope.identity.rateCard.id,
              language: currentScope.identity.language,
            })
          )
        } else {
          this.store.dispatch(
            ScopeOverviewActions.getAvailableScopeDeliverableTypes({
              mergeLibraryDeliverableEntryStrategy: 'DISCIPLINE',
            })
          )
        }
      },
    }

    this.dialog.open(TrackTradeModalComponent, { data: dialogConfig })
  }

  addRolesModal(currentScope: ScopeVersion) {
    const data: AddRolesModalConfig = {
      title: `Add/Update Role(s)`,
      rateCard$: this.rateCard$,
      currentScope: currentScope,
    }
    this.dialog.open(AddRolesModalComponent, { data })
  }

  completeTradeModal(deliverable: Deliverable, currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Complete Deliverable',
      body: `Are you sure you want to mark ${deliverable.name} as complete?`,
      confirmText: 'Complete',
      confirmCallback: () => {
        this.store.dispatch(
          ScopeOverviewActions.completeTrade({ deliverableId: deliverable.id, scopeId: currentScope.identity.id })
        )
        dialog.close()
      },
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  stopProgressTradeModal(deliverable: Deliverable, currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Stop Progress of Deliverable',
      body: `Are you sure you want to stop progress of ${deliverable.name}?`,
      confirmText: 'Stop',
      confirmCallback: () => {
        this.store.dispatch(
          ScopeOverviewActions.stopTrade({ deliverableId: deliverable.id, scopeId: currentScope.identity.id })
        )
        dialog.close()
      },
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  confirmTradeModal(deliverable: Deliverable) {
    const data: ModalConfig = {
      title: 'Confirm Trade',
      body: `Are you sure you want to confirm trade for ${deliverable.name}?`,
      confirmText: 'Confirm Trade',
      confirmCallback: () => {
        this.store.dispatch(
          ScopeOverviewActions.confirmTrade({ scopeId: this.currentScope.identity.id, deliverableId: deliverable.id })
        )
        dialog.close()
      },
      limitBodyWidth: true,
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  cancelTradeModal(deliverable: Deliverable, currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Confirm Trade',
      body: `Are you sure you want to cancel trade for ${deliverable.name}?`,
      confirmText: 'Cancel Trade',
      confirmCallback: () => {
        this.store.dispatch(
          ScopeOverviewActions.cancelTrade({ deliverableId: deliverable.id, scopeId: currentScope.identity.id })
        )
        dialog.close()
      },
      cancelCallback: undefined,
      inputs: [],
      isMemberModal: undefined,
      limitBodyWidth: true,
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  reopenTradeModal(deliverable: Deliverable, currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Confirm Trade',
      body: `Are you sure you want to reopen for ${deliverable.name}?`,
      confirmText: 'Reopen',
      confirmCallback: () => {
        this.store.dispatch(
          ScopeOverviewActions.reopenTrade({ deliverableId: deliverable.id, scopeId: currentScope.identity.id })
        )
        dialog.close()
      },
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  refreshActuals(currentScope: ScopeVersion) {
    const trafficSystem =
      this.loggedInUser.company.trafficSystemSettings != null
        ? this.loggedInUser.company.trafficSystemSettings.trafficSystem
        : null
    const timesheetConfigType = trafficSystem && trafficSystem.externalTrafficSystemConfig.scopeTimeSheet
    if (timesheetConfigType == null) return
    if (timesheetConfigType.type === 'SOW_UPLOAD') {
      this.snackbarService.showInfoSnackbar('Refreshing actuals. Do not refresh the page.')
      this.scopeApprovalFlowService.isApprovalLoadingSource$.next(true)
      this.scopeOverviewService.refreshActuals(currentScope.scopeOfWorkVersion.identity.id).subscribe({
        next: () => {
          this.snackbarService.showInfoSnackbar('Actuals are updated successfully.')
          this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
          this.scopeApprovalFlowService.isApprovalLoadingSource$.next(false)
        },
        error: (error) => {
          if (error.status === 500 && error.data == '') {
            this.snackbarService.showErrorSnackbar(
              'An error occurred. Please try again. ' +
                'Check if job code property is set for deliverables in this SOW' +
                error.data
            )
          } else if (error.status == 400 && error.data != '') {
            this.showConflictDatesModal(error)
          } else {
            this.snackbarService.showDefaultErrorSnackbar()
          }
          this.scopeApprovalFlowService.isApprovalLoadingSource$.next(false)
          this.cdr.detectChanges()
        },
      })
    } else if (timesheetConfigType.type === 'URL') {
      this.scopeOverviewService.importScopeTimeSheets(this.currentScope.identity.id).subscribe({
        next: () => this.snackbarService.showInfoSnackbar('Actuals update request was sent.'),
        error: () => this.snackbarService.showDefaultErrorSnackbar(),
      })
    }
  }

  showConflictDatesModal(error: any) {
    let desc = ''
    const conflicts = JSON.parse(error.error)
    Object.keys(conflicts).forEach((code) => {
      conflicts[code].forEach((d) => {
        desc +=
          '<tr>' +
          '<td>' +
          d['deliverableName'] +
          '</td>' +
          '<td>' +
          d['externalId'] +
          '</td>' +
          '<td>' +
          d['startDate'].day +
          '/' +
          d['startDate'].month +
          '/' +
          d['startDate'].year +
          '</td>' +
          '<td>' +
          d['endDate'].day +
          '/' +
          d['endDate'].month +
          '/' +
          d['endDate'].year +
          '</td>' +
          '</tr>'
      })
    })
    const table =
      `<div class="conflict-container"><table class="conflict-table">
                    <tbody>
                    <tr class="conflict-title">
                        <td>Name</td>
                        <td>Job code</td>
                        <td>Start date</td>
                        <td>End date</td>
                    </tr>` +
      desc +
      `</tbody>
                    </table></div>`

    this.scopeApprovalFlowService.isApprovalLoadingSource$.next(false)
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Error - found conflicting dates in deliverables`,
        body: table,
        confirmText: 'Close',
        confirmCallback: () => dialog.close(),
        inlineHtmlBody: true,
      },
    })
  }

  showDeliverableApplyLocationCardDialog(event: { deliverable: Deliverable; rateCard: RatecardVersion }) {
    const modalConfig: ModalConfig = {
      title: `Apply location card to ${this.lang.get('deliverable')}`,
      body: `All roles within the ${this.lang.get('deliverable')} will be assigned with the rate and cost of the location card. The default ratecard rate and cost will be applied where there is no role within the location card.`,
      confirmText: 'Apply',
      confirmCallback: (form: FormControl, selections: any[]) => {
        this.setApprovalLoading(true)
        this.scopeOverviewService
          .applyLocationCardToDeliverable(this.currentScope.identity.id, event.deliverable.id, selections[0].id)
          .subscribe({
            next: (deliverable) => {
              this.store.dispatch(ScopePaginatedOverviewActions.getScopeDeliverableSuccess({ deliverable }))
              this.setApprovalLoading(false)
              dialog.close()
            },
            error: (error) => {
              this.snackbarService.showErrorSnackbar(`An error occurred while applying the location card`)
              console.error(error)
              this.setApprovalLoading(false)
              dialog.close()
            },
          })
      },
      inputs: [
        {
          name: 'locationCard',
          control: new FormControl(''),
          type: 'autocomplete',
          label: 'Location Card',
          placeholder: 'Location Card',
          options: event.rateCard.locationCards,
          multiselect: false,
          optionFn: (location: any) => location?.rateCardLocation?.name,
        },
      ],
      limitBodyWidth: true,
      isFormValid: (form: FormControl, selections: any[]) => !!selections.length,
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  addThirdPartyCost(currentScope: ScopeVersion) {
    this.dialog.open(AddTpcModalComponent, { data: { currentScope } })
  }

  setupDebouncedUpdate() {
    this.updateSubject
      .pipe(
        debounceTime(Timeout.S),
        switchMap(({ event }) =>
          this.scopeOverviewService.updateScopeThirdPartyCost(this.currentScope.identity.id, event)
        )
      )
      .subscribe(
        (response) => {
          this.store.dispatch(ScopeOverviewActions.updateScopeTpcSuccess({ thirdPartyCost: response }))
        },
        (error) => {
          console.error('Update error:', error)
        }
      )
  }

  updateThirdPartyCost(event: ThirdPartyCost) {
    this.updateSubject.next({ event })
  }

  addDiscount(scope: ScopeVersion) {
    const dialog = this.dialog.open(AddEditDiscountModalComponent, {
      data: {
        currencyCode: scope.identity.rateCard.currencyCode,
        discount: scope.discount,
        maxDiscount: scope.getNaturalSellingPrice({ includeTpc: true }).amount,
        onSubmit: (discount: any) => {
          dialog.close()
          this.scopeOverviewService.updateScopeDiscount(this.currentScope.identity.id, discount).subscribe({
            next: () => {
              this.store.dispatch(ScopeOverviewActions.updateScopeDiscountSuccess({ discount: discount }))
            },
            error: (error) => {
              if (error.error?.errorCode == 'DISCOUNT_NOT_WITHIN_RANGE') {
                this.snackbarService.showPrioritisedSnackbar(
                  'Discount exceeds permitted range ' + error.error.message,
                  5000,
                  SnackbarEventType.ERROR
                )
              } else {
                this.snackbarService.showDefaultErrorSnackbar()
              }
              return error
            },
          })
        },
      },
    })
  }

  deleteDiscount(currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Delete discount',
      body: `Are you sure you want to delete discount?`,
      confirmText: 'Delete',
      confirmCallback: () => {
        this.store.dispatch(ScopeOverviewActions.deleteScopeDiscount({ scopeId: currentScope.identity.id }))
        dialog.close()
      },
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  editMsaLineItem(scope: ScopeVersion) {
    const dialog = this.dialog.open(EditMsaModalComponent)
    dialog.componentInstance.currencyCode = scope.identity.rateCard.currencyCode
    dialog.componentInstance.msaLineItem = scope.msaLineItem
    dialog.componentInstance.onSubmit.subscribe((msaLineItem: any) => {
      dialog.close()
      this.scopeOverviewService.updateScopeMsa(this.currentScope.identity.id, msaLineItem).subscribe({
        next: () => {
          this.store.dispatch(ScopeOverviewActions.updateScopeMsaSuccess({ msaLineItem: msaLineItem }))
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
    })
  }

  deleteMsaLineItem(card: any, currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: 'Remove MSA',
      body: `Are you sure you want to permanently remove the MSA from this ${this.lang.get('scope|l')}?`,
      confirmText: 'Remove',
      confirmCallback: (form: FormControl) => {
        this.store.dispatch(
          ScopeOverviewActions.deleteScopeMsa({ scopeId: currentScope.identity.id, reason: form.get('reason')?.value })
        )
        if (card) {
          const deletedCard = { ...card, disabled: true }
          Object.assign(card, deletedCard)
        }
        dialog.close()
      },
      inputs: [{ name: 'reason', control: new FormControl(''), type: 'textarea', label: 'Reason', maxLength: 250 }],
      limitBodyWidth: true,
      isFormValid: (form: FormControl) => !!form.get('reason')?.value,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  openAcceptDeliverablesModal(key: string) {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((currentScope) => !!currentScope)
      )
      .subscribe((currentScope) => {
        this.completeReviewDialogConfig.close()

        const deliverableIds = this.scopeApprovalFlowService
          .getReviewSummaryRowData(currentScope)
          .rowData.items.map((deliverable: Deliverable) => deliverable.id)

        const title =
          key === 'openAcceptDeliverableDialog' ? 'Accept All Deliverable Reviews' : 'Reject All Deliverable Reviews'
        const buttonText = key === 'openAcceptDeliverableDialog' ? 'Accept All Deliverables' : 'Reject All Deliverables'

        let acceptComment: string

        const acceptDeliverableDialogConfig: ModalConfig = {
          title,
          body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
          confirmText: buttonText,
          confirmCallback: (form: FormControl) => {
            acceptComment = form.get('comment').value
            if (key === 'openAcceptDeliverableDialog') {
              this.setApprovalLoading(true)
              this.approvalsService
                .acceptAllDeliverableReview(this.currentScope.identity.id, acceptComment, deliverableIds)
                .pipe(this.destroy$())
                .subscribe({
                  next: (deliverables: Deliverable[]) => {
                    this.updateDeliverablesAndOpenCompleteReviewModal(dialog, currentScope, deliverables, acceptComment)
                    this.reloadTasks(currentScope)
                  },
                  error: (error: any) => {
                    this.setApprovalLoading(false)
                    this.snackbarService.showDefaultErrorSnackbar()
                    console.error(JSON.stringify(error))
                    return error
                  },
                })
            } else if (key === 'openRejectDeliverableDialog') {
              this.setApprovalLoading(true)
              const acceptComment: string = form.get('comment').value
              this.approvalsService
                .rejectAllDeliverableReview(this.currentScope.identity.id, acceptComment, deliverableIds)
                .pipe(this.destroy$())
                .subscribe({
                  next: (deliverables: Deliverable[]) => {
                    this.updateDeliverablesAndOpenCompleteReviewModal(dialog, currentScope, deliverables, acceptComment)
                    this.reloadTasks(currentScope)
                  },
                  error: (error: any) => {
                    this.setApprovalLoading(false)
                    this.snackbarService.showDefaultErrorSnackbar()
                    console.error(JSON.stringify(error))
                    return error
                  },
                })
            }
          },
          cancelCallback: undefined,
          inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
          isMemberModal: undefined,
          isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
          limitBodyWidth: true,
        }

        const dialog = this.dialog.open(ScopeUiModalComponent, { data: acceptDeliverableDialogConfig })
      })
  }

  updateDeliverablesAndOpenCompleteReviewModal(
    dialog: MatDialogRef<any>,
    currentScope: ScopeVersion,
    deliverables: Deliverable[],
    acceptComment: string
  ) {
    deliverables.forEach((update) => {
      const deliverable = currentScope.deliverables.find((d) => d.id === update.id)
      this.store.dispatch(
        ScopeOverviewActions.updateScopeDeliverableSuccess({
          deliverable: plainToInstance(Deliverable, { ...deliverable, reviewers: update.reviewers }),
        })
      )
    })

    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(take(1))
      .subscribe((currentScope) => {
        this.scopeApprovalFlowService.updateApprovalStateChecks(currentScope)
        this.setApprovalLoading(false)
        dialog.close()
        this.completeReviewModal(currentScope, acceptComment)
      })
  }

  completeReviewModal(currentScope: ScopeVersion, comment?: string) {
    const data: ReviewSubmitModalConfig = {
      currentScope: currentScope,
      currentUser: this.loggedInUser!,
      deliverables: currentScope.deliverables,
      deliverableColumns: [{ key: 'DELIVERABLE' }, { key: 'REVIEW_STATUS' }] as Preference[],
      deliverableTableData: this.scopeApprovalFlowService.getReviewSummaryRowData(currentScope),
      stateChecks: this.scopeApprovalFlowService.stateChecks,
      title: `Complete ${this.lang.get('scope')} Review`,
      limitModalWidth: true,
      text: comment,
      customContainerClass: 'w-575',
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
    }

    this.completeReviewDialogConfig = this.dialog.open(ReviewSubmitModalComponent, { data })
  }

  addTask(ratecardVersionId: number) {
    const dialog = this.dialog.open(AddTaskModalComponent)
    dialog.componentInstance.taskTemplates$ = this.scopeOverviewService.getTaskTemplates(ratecardVersionId)
    dialog.componentInstance.rateCards$ = this.store
      .select(ScopingSelectors.selectRateCard)
      .pipe(map((ratecard) => (ratecard ? [ratecard] : [])))

    // Get current scope and pass its dates to the modal
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((scope) => !!scope)
      )
      .subscribe((currentScope) => {
        dialog.componentInstance.scopeStartDate = currentScope.startDate
        dialog.componentInstance.scopeEndDate = currentScope.endDate
      })

    dialog.componentInstance.onSubmit.subscribe((task: NewTask) => {
      dialog.close()
      this.scopeOverviewService.addTask(this.currentScope.identity.id, task).subscribe({
        next: (task) => {
          this.store.dispatch(ScopeOverviewActions.addScopeDeliverableSuccess({ deliverable: task }))
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          return error
        },
      })
    })
    dialog.componentInstance.onSubmitSavedTasks.subscribe((tasks: number[]) => {
      dialog.close()
      this.scopeOverviewService.addTasksFromLibrary(this.currentScope.identity.id, tasks).subscribe({
        next: (tasks) => {
          this.store.dispatch(ScopeOverviewActions.addScopeDeliverablesSuccess({ deliverables: tasks }))
          this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          return error
        },
      })
    })
  }

  agreeToScopeModal(currentScope: ScopeVersion) {
    const hasOverrideCollaborators =
      this.scopeApprovalFlowService.isSuperApprover(this.loggedInUser, currentScope) &&
      this.scopeApprovalFlowService.stateChecks.currentStateMembers?.length > 1
    const data: ModalConfig = {
      title: `Agree ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Accept',
      cancelText: hasOverrideCollaborators ? 'Accept for all Approvers' : undefined,
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const acceptComment = form.get('comment').value
        this.approvalsService
          .agreeToScope(this.currentScope.identity.id, undefined, acceptComment)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      cancelCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const acceptComment = form.get('comment').value
        this.approvalsService
          .agreeToScope(this.currentScope.identity.id, hasOverrideCollaborators, acceptComment)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
      hasCancelButton: hasOverrideCollaborators,
      flatButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  declineScopeModal() {
    const data: ModalConfig = {
      title: `Reject ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Decline',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const acceptComment = form.get('comment').value
        this.approvalsService
          .rejectScope(this.currentScope.identity.id, acceptComment)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(
                ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id })
              )
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  acceptScopeModal() {
    const data: ModalConfig = {
      title: `Client Approve ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Accept',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const acceptComment = form.get('comment').value
        this.approvalsService
          .clientAcceptScope(this.currentScope.identity.id, acceptComment)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  rejectScopeModal() {
    const data: ModalConfig = {
      title: `Client Reject ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Decline for revision',
      cancelText: 'Decline and Close',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const acceptComment = form.get('comment').value
        this.approvalsService
          .clientRejectScope(this.currentScope.identity.id, acceptComment, false)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      cancelCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const rejectComment = form.get('comment').value
        this.approvalsService
          .clientRejectScope(this.currentScope.identity.id, rejectComment, true)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
      hasCancelButton: true,
      flatButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  closeScopeModal() {
    const data: ModalConfig = {
      title: `Close ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')}`,
      confirmText: 'Submit',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const closeComment = form.get('comment').value
        this.approvalsService
          .closeScope(this.currentScope.identity.id, closeComment)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(JSON.stringify(error))
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  exportScopeByRole(currentScope: ScopeVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Export Excel document`,
        `Do you want to download ${this.lang.get('scope|l')} "${currentScope.name}" as Excel document?`,
        'Export',
        undefined,
        () => {
          dialog.close()

          const warningDialogRef = this.dialog.open(ScopeUiModalComponent, {
            data: {
              title: `Export ${this.lang.get('scope')}`,
              body: 'Your document is exporting, please DO NOT CLICK "download" again. Check your Notifications tab in a few minutes for the download',
              confirmText: 'OK',
              confirmCallback: () => warningDialogRef.close(),
              limitBodyWidth: true,
            },
          })

          this.scopeOverviewService.exportScopeByRoleToExcel(this.currentScope.identity.id).subscribe({
            next: () => {
              this.snackbarService.showInfoSnackbar('Download will start soon.', SNACKBAR_LENGTH_SHORT)
            },
            error: (error: any) => {
              this.snackbarService.showDefaultErrorSnackbar()
              return error
            },
          })
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  deleteScopeThirdPartyCost(tpc: ThirdPartyCost) {
    const data: ModalConfig = {
      title: 'Delete TPC',
      body: `Are you sure you want to delete third party cost?`,
      confirmText: 'Delete',
      confirmCallback: () => {
        this.scopeOverviewService.deleteScopeThirdPartyCost(this.currentScope.identity.id, tpc.id).subscribe({
          next: () => {
            this.store.dispatch(ScopeOverviewActions.deleteScopeTpcSuccess({ thirdPartyCost: tpc }))
          },
          error: () => console.log('err'),
        })

        dialog.close()
      },
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  openTrafficExportModal(currentScope: ScopeVersion) {
    if (!this.traffickingService.isTrafficking && this.traffickingService.deliverableErrors.length === 0) {
      this.traffickingService.setConfirmationPending()
    }

    if (!this.companyIntegrations || this.companyIntegrations.length == 0) {
      const dialogConfig: ExportModalConfig = {
        title: 'Export Traffic',
        body: `Are you sure you want to export your ${this.lang.get('scope')} for Trafficking?`,
        currentScope: currentScope,
        currentUser: this.loggedInUser,
        limitModalWidth: true,
        scopeId: this.currentScope.identity.id,
      }
      this.dialog.open(ExportTrafficModal, { data: dialogConfig })
    } else {
      const dialogConfig: IntegrationModalConfig = {
        title: 'Scope Traffic',
        body: `Select to which systems you want to send your ${this.lang.get('scope')} data`,
        integrations: this.companyIntegrations,
        currentScope: currentScope,
        currentUser: this.loggedInUser,
        limitModalWidth: true,
        scopeId: this.currentScope.identity.id,
      }
      this.dialog.open(IntegrationTrafficModal, { data: dialogConfig })
    }
  }

  exportTrafficThroughAgencyTrafficSystem() {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((currentScope) => !!currentScope)
      )
      .subscribe((currentScope) => {
        if (this.scopeApprovalFlowService.isTrafficDisabled(currentScope)) return

        this.traffickingService.assertActiveSystemConfigured(this.loggedInUser.company)
        this.exportToAgencyTrafficSystem()
      })
  }

  exportToAgencyTrafficSystem() {
    if (this.loggedInUser.company.trafficSystemSettings.trafficSystem.name == 'Workato') {
      this.store.dispatch(
        ScopeOverviewActions.exportTrafficScopeWorkato({
          target: 'WORKDAY__PROJECT',
          identityId: this.currentScope.identity.id,
        })
      )
    } else {
      this.store.dispatch(ScopeOverviewActions.exportTrafficScope({ identityId: this.currentScope.identity.id }))
    }
  }

  exportTrafficThroughWorkato() {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((currentScope) => !!currentScope)
      )
      .subscribe((currentScope) => {
        if (this.scopeApprovalFlowService.isTrafficDisabled(currentScope)) return

        if (this.loggedInUser.company.hasWorkatoIntegration) {
          this.store.dispatch(
            ScopeOverviewActions.trafficScopeThroughWorkato({ identityId: this.currentScope.identity.id })
          )
        }
      })
  }

  submitDraftScopeModal(currentScope: ScopeVersion) {
    const canOverrideCollaborators =
      this.scopeApprovalFlowService.isSuperCollaborator(this.loggedInUser, currentScope) &&
      this.scopeApprovalFlowService.stateChecks.currentStateMembers?.length > 1
    const dialogConfig: ModalConfig = {
      title: `Submit ${this.lang.get('scope')}`,
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Submit my own work',
      cancelText: canOverrideCollaborators ? "Submit everyone's work" : undefined,
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const approvalText: string = form.get('comment').value
        this.approvalsService
          .submitScope(this.currentScope.identity.id, false, approvalText)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      cancelCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const approvalText: string = form.get('comment').value
        this.approvalsService
          .submitScope(this.currentScope.identity.id, true, approvalText)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: scope }))
              this.reloadTasks(scope)
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isMemberModal: undefined,
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
      hasCancelButton: canOverrideCollaborators,
      flatButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: dialogConfig })
  }

  withdrawSubmissionModal() {
    const dialogConfig: ModalConfig = {
      title: 'Withdraw my submission',
      body: `Add a note here to help other collaborators understand your work on this ${this.lang.get('scope')} and guide them with their next steps`,
      confirmText: 'Withdraw my submission',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const approvalText: string = form.get('comment').value
        this.approvalsService
          .withdrawSubmission(this.currentScope.identity.id, approvalText)
          .pipe(this.destroy$())
          .subscribe({
            next: (scope: ScopeVersion) => {
              this.setApprovalLoading(false)
              this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
              this.store.dispatch(
                ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id })
              )
              dialog.close()
            },
            error: (error: any) => {
              this.setApprovalLoading(false)
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      cancelCallback: undefined,
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isMemberModal: undefined,
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: dialogConfig })
  }

  submitScopeReview(approvalText: string, hasOverride: boolean): void {
    this.setApprovalLoading(true)
    this.approvalsService
      .submitScopeReview(this.currentScope.identity.id, hasOverride, approvalText)
      .pipe(this.destroy$())
      .subscribe({
        next: (scope: ScopeVersion) => {
          this.setApprovalLoading(false)
          this.scopeApprovalFlowService.updateApprovalStateChecks(scope)
          this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
          this.completeReviewDialogConfig.close()
        },
        error: (error: any) => {
          this.setApprovalLoading(false)
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
          return error
        },
      })
  }

  sendNotificationReminder(currentScope: ScopeVersion) {
    const data: ModalConfig = {
      title: currentScope.status === 'SUBMITTED' ? 'Send Approval Reminder' : 'Send Review Reminder',
      body: "You can send approval reminder email to the Approvers if you wish to make sure that the scopes approvals dosen't get forgotten",
      confirmText: 'Send Reminder',
      confirmCallback: (form: FormControl) => {
        const approvalText: string = form.get('comment').value
        this.store.dispatch(
          ScopeOverviewActions.sendNotificationReminder({
            scopeId: this.currentScope.identity.id,
            userId: this.loggedInUser.id,
            comment: approvalText,
          })
        )
        dialog.close()
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', placeholder: 'Write a comment' }],
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  showAddTeamMemberModal(currentScope: ScopeVersion) {
    if (this.scopeApprovalFlowService.isStateDraft(currentScope.status))
      return this.scopeTeamService.showAddCollaboratorModal(currentScope, this.cdnConfig)

    if (this.scopeApprovalFlowService.isStateInReview(currentScope.status))
      return this.scopeTeamService.showAddReviewerModal(currentScope, this.cdnConfig)

    if (this.scopeApprovalFlowService.isStateReviewed(currentScope.status))
      return this.scopeTeamService.showAddApproverModal(currentScope, this.cdnConfig)

    if (
      this.scopeApprovalFlowService.isStateAgencyApproved(currentScope.status) &&
      !this.scopeApprovalFlowService.isStateApproved(currentScope)
    )
      return this.scopeTeamService.showAddApproverModal(currentScope, this.cdnConfig)

    if (this.scopeApprovalFlowService.isStateApproved(currentScope))
      return this.scopeTeamService.showAddTraffickerModal(currentScope, this.cdnConfig)
  }

  openExportModal(currentScope: ScopeVersion) {
    const dialogConfig: ExportModalConfig = {
      title: `Export ${this.lang.get('scope')}`,
      body: `Select which document file you want to output your ${this.lang.get('scope|l')}:`,
      currentScope: currentScope,
      currentUser: this.loggedInUser,
      limitModalWidth: true,
      scopeId: this.currentScope.identity.id,
    }

    this.dialog.open(ExportModalComponent, { data: dialogConfig })
  }

  openCreateChangeRequestModal() {
    const data: ModalConfig = {
      title: `Create Change Request`,
      body: `Create descriptive short name for internal reference`,
      confirmText: 'Save',
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const payload: ChangeRequestCreatePayload = form.value
        this.store.dispatch(
          ChangeRequestActions.createChangeRequest({
            payload,
            callback: () => {
              this.setApprovalLoading(false)
              dialog.close()
            },
          })
        )
      },
      inputs: [
        { name: 'name', control: new FormControl(''), type: 'text', label: 'Name' },
        { name: 'description', control: new FormControl(''), type: 'textarea', label: 'Description' },
      ],
      isFormValid: (form: FormControl) => !!form.get('name')?.value && !!form.get('description')?.value,
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  private setApprovalLoading = (value: boolean) => {
    this.scopeApprovalFlowService.isApprovalLoadingSource$.next(value)
    this.cdr.detectChanges()
  }

  private getLatestAndUpdateState = () => {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(take(1))
      .subscribe((currentScope) => {
        this.scopeApprovalFlowService.updateApprovalStateChecks(currentScope)
      })
  }

  private getCurrentRatecard(): void {
    this.scopeOverviewService.getRatecardVersion(this.currentScope.identity.rateCard.id).subscribe({
      next: (result) => {
        this.currentRatecard = result
        if (this.scopeApprovalFlowService.isScopeEditable(this.currentScope)) {
          this.store.dispatch(ScopeActions.getRateCardForScopeSuccess({ ratecard: this.currentRatecard }))
        }
      },
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        return error
      },
    })
  }

  private getRetainedTeamStats(): void {
    if (this.loggedInUser?.company.hasApplicationSetting('SOW__RETAINED_HOURS')) {
      this.initScopeRetainedStats(this.currentScope)
      this.store
        .select(ScopeOverviewSelectors.selectRetainedTeamStats)
        .pipe(
          filter((stats) => !!stats),
          take(1)
        )
        .subscribe((stats) => {
          this.showUseRetainedTeamToggle = this.resolveShowUseRetainedTeamToggle(
            stats,
            this.currentScope.identity.rateCard.id
          )
        })
    }
  }

  private reloadTasks(scope: ScopeVersion) {
    if (scope.isScopeByRole()) {
      this.store.dispatch(
        ScopeOverviewActions.getAllTasks({
          scopeId: this.currentScope.identity.id,
          scopeVersion: scope.version,
        })
      )
    }
  }

  private isDeliverable = (value: any): value is Deliverable => (value as Deliverable).id !== undefined

  private destroy$
  protected readonly ScopeOverviewTab = ScopeOverviewTab
  protected readonly resolveShowUseRetainedTeamToggle = resolveShowUseRetainedTeamToggle
}
